import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Scale, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground mb-6 leading-relaxed text-justify transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const PoliticalStatusCorrectionPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
              <Scale className="h-8 w-8 text-gold-light" />
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Political Status Correction
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Remove yourself from federal jurisdiction and reclaim your constitutional American statehood
            </p>
          </div>

            <div className="prose prose-lg max-w-none">
            <AnimatedParagraph delay={200}>
              Political Status Correction is the foundational process that allows you to reclaim your true constitutional status as an American state national, removing yourself from federal jurisdiction and the presumptions that bind you to corporate governance.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={600}>
              This process restores your rights under Natural Law and constitutional principles, freeing you from the bonds of federal citizenship and returning you to your sovereign status as intended by the founders.
            </AnimatedParagraph>

            <div className="mt-12 mb-8">
              <h2 className="font-rockwell text-3xl font-semibold text-primary mb-6">
                Step 1: U.S. Citizen v. American National: The Hidden Contract
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                There are two radically different statuses you can adopt as an American that sound similar—but place you in completely different jurisdictions:
              </p>
              
              <ul className="list-disc list-inside mb-6 text-muted-foreground space-y-2">
                <li><strong>"US citizen"</strong> under the 14th Amendment</li>
                <li><strong>"American national"</strong> of the United States of America Republic, also known as a constitutional American</li>
              </ul>
              
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Most of us were never told what a US Citizen actually is. But once you know—you must choose.
              </p>

              <div className="mb-8">
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  1. 14th Amendment "U.S. Citizen" – A Subject of the Corporate State
                </h3>
                
                <p className="text-muted-foreground mb-4">
                  A U.S. citizen, as defined by the 14th Amendment, is not what you think it is.
                </p>
                
                <p className="text-muted-foreground mb-4">
                  It is not someone born in the land of the united States of America and protected by common law. It is a federal status, created after the Civil War, and it places you under a commercial, administrative jurisdiction—not constitutional, not common law, not natural law.
                </p>
                
                <p className="text-muted-foreground mb-4">When you accept or presume this status, you:</p>
                
                <ul className="list-disc list-inside mb-4 text-muted-foreground space-y-2 ml-4">
                  <li>Become a subject of Congress, not a sovereign of the land.</li>
                  <li>Are governed by public policy, not natural rights.</li>
                  <li>Are treated as a corporate entity—a legal fiction, not a living man or woman.</li>
                  <li>Are presumed to have voluntarily waived your God-given rights in exchange for government privileges and benefits.</li>
                </ul>
                
                <p className="text-muted-foreground mb-6 font-semibold">
                  This is the hidden contract. It is voluntary, yet it's presumed unless you rebut it.
                </p>
              </div>

              <div className="mb-8">
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  2. American National – Standing on the Land in Full Capacity
                </h3>
                
                <p className="text-muted-foreground mb-4">
                  An American national of the united States of America (note the lowercase "u") is a man or woman born on the land of the republic—not a subject of the federal corporation.
                </p>
                
                <p className="text-muted-foreground mb-4">As an American national:</p>
                
                <ul className="list-disc list-inside mb-4 text-muted-foreground space-y-2 ml-4">
                  <li>You are governed by God's law, Natural Law, and the original Constitution.</li>
                  <li>You do not fall under federal territorial jurisdiction, unless you consent.</li>
                  <li>You retain all unalienable rights, including life, liberty, property, travel, and self-governance.</li>
                  <li>You correct the record to reflect your true status—and you live free from presumptive contracts and obligations created by legal fiction.</li>
                </ul>
                
                <div className="bg-primary/5 p-6 rounded-lg border border-primary/10 mb-6">
                  <p className="text-muted-foreground font-semibold text-center">
                    American nationals are the creditors.<br/>
                    14th Amendment citizens are the debtors.<br/>
                    American nationals are trustees of the republic.<br/>
                    U.S. citizens are beneficiaries of the state—and in law, beneficiaries are controlled.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  The Serious Implications:
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-3">If you operate as a 14th Amendment U.S. citizen:</h4>
                    <ul className="list-disc list-inside text-red-700 space-y-2 text-sm">
                      <li>You are subject to IRS codes, administrative courts, and federal regulations.</li>
                      <li>You can be taxed without direct apportionment, summoned, fined, and treated as an asset of the U.S. Corporation.</li>
                      <li>Your children, land, and labor are presumed collateral in the national debt system.</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-3">If you stand as an American national:</h4>
                    <ul className="list-disc list-inside text-green-700 space-y-2 text-sm">
                      <li>You are a private man/woman, outside commercial jurisdiction.</li>
                      <li>You are protected by the law of the land, not statutes made for franchises.</li>
                      <li>You can reserve your rights, control your property, and operate freely—as long as you cause no harm.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  This Is Not Theory. It's Jurisdictional Reality.
                </h3>
                
                <p className="text-muted-foreground mb-4">
                  The difference isn't just philosophical—it's jurisdictional, legal, and spiritually binding.
                </p>
                
                <p className="text-muted-foreground mb-4">
                  Every time you sign a government form, take a benefit, or use a title (like "U.S. citizen"), you're presumed to be in contract. But you can correct your status, declare your truth, record your standing, and live as the free man or woman you were born to be.
                </p>
                
                <div className="bg-primary/5 p-6 rounded-lg border border-primary/10 text-center">
                  <p className="text-muted-foreground mb-2"><strong>The choice is yours:</strong></p>
                  <p className="text-muted-foreground mb-2">Be a citizen-slave under federal franchise...</p>
                  <p className="text-muted-foreground mb-4"><strong>Or be a free national of the republic, standing under God.</strong></p>
                  <p className="text-primary font-semibold">This is about restoring your name, your standing, and your inheritance.</p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-8 bg-primary/5 rounded-lg border border-primary/10">
              <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4">
                Ready to Reclaim Your Status?
              </h3>
              <p className="text-muted-foreground mb-6">
                Learn more about the Political Status Correction process and how it can restore your constitutional rights.
              </p>
              <div className="flex flex-col gap-4 items-start">
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Link to="/work-with-us" className="flex items-center gap-2">
                    Work with Us
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/education" className="flex items-center gap-2">
                    Do It Yourself
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PoliticalStatusCorrectionPage;